/**
 * Google Tag Manager event tracking utilities
 */
import { formatToHumanReadable, formatWeekdayLong } from "./format-data";
import { PatientType, SelfScheduleStep } from "@/stores/self-schedule-store";

/**
 * Push an event to Google Tag Manager dataLayer
 */
export const pushGTMEvent = (
  eventName: string,
  eventData?: Record<string, any>,
) => {
  if (typeof window !== "undefined" && window.dataLayer) {
    window.dataLayer.push({
      event: eventName,
      ...eventData,
    });
  }
};

/**
 * Track self-schedule step progression
 */
export const trackSelfScheduleStep = (
  step: SelfScheduleStep,
  additionalData?: Record<string, any>,
) => {
  const stepNames: Record<SelfScheduleStep, string> = {
    [SelfScheduleStep.PATIENT_TYPE]: "Patient Type Selection",
    [SelfScheduleStep.LOCATION_TYPE]: "Location Type Selection",
    [SelfScheduleStep.SERVICE]: "Service Selection",
    [SelfScheduleStep.APPOINTMENT_REVIEW]: "Appointment Review",
    [SelfScheduleStep.LOCATION]: "Location Selection",
    [SelfScheduleStep.TIME]: "Time Selection",
    [SelfScheduleStep.CONFIRMATION]: "Appointment Confirmation",
    [SelfScheduleStep.SECURE_APPOINTMENT]: "Patient Information",
    [SelfScheduleStep.CHECKOUT]: "Payment Checkout",
    [SelfScheduleStep.BOOKING_COMPLETE]: "Booking Complete",
    [SelfScheduleStep.WAITLIST_COMPLETE]: "Waitlist Complete",
    [SelfScheduleStep.PAYMENT_METHOD]: "Payment Method Selection",
  };

  pushGTMEvent("self_schedule_step", {
    step_name: stepNames[step],
    step_id: step,
    ...additionalData,
  });
};

/**
 * Track service selection
 */
export const trackServiceSelection = (
  serviceId: string,
  serviceName?: string,
  patientType?: PatientType | null,
) => {
  pushGTMEvent("self_schedule_service_selected", {
    service_id: serviceId,
    service_name: serviceName,
    patient_type: formatToHumanReadable(patientType),
  });
};

/**
 * Track provider selection
 */
export const trackProviderSelection = (
  providerId: string,
  providerName?: string,
  patientType?: PatientType | null,
  additionalData?: Record<string, any>,
) => {
  pushGTMEvent("self_schedule_provider_selected", {
    provider_id: providerId,
    provider_name: providerName,
    patient_type: formatToHumanReadable(patientType),
    ...additionalData,
  });
};

/**
 * Track location selection
 */
export const trackLocationSelection = (
  locationId: string,
  locationName?: string,
  patientType?: PatientType | null,
) => {
  pushGTMEvent("self_schedule_location_selected", {
    location_id: locationId,
    location_name: locationName,
    patient_type: formatToHumanReadable(patientType),
  });
};

/**
 * Track time slot selection
 */
export const trackTimeSelection = (
  dateTime: Date,
  patientType?: PatientType | null,
) => {
  pushGTMEvent("self_schedule_time_selected", {
    selected_date: dateTime.toISOString().split("T")[0],
    selected_time: dateTime.toTimeString().split(" ")[0],
    day_of_week: formatWeekdayLong(dateTime),
    patient_type: formatToHumanReadable(patientType),
  });
};

/**
 * Track appointment booking completion (after payment or if no fee required)
 * @param bookingFeeAmount - Booking fee amount in dollars (not cents)
 */
export const trackBookingComplete = (
  appointmentId: string,
  patientType?: PatientType | null,
  serviceName?: string,
  locationName?: string,
  bookingFeeAmount?: number,
) => {
  pushGTMEvent("self_schedule_booking_complete", {
    appointment_id: appointmentId,
    patient_type: formatToHumanReadable(patientType),
    service_name: serviceName,
    location_name: locationName,
    booking_fee_amount: bookingFeeAmount ?? 0,
    timestamp: new Date().toISOString(),
  });
};

/**
 * Track booking abandonment (user leaves the flow)
 */
export const trackBookingAbandonment = (lastStep: SelfScheduleStep) => {
  pushGTMEvent("self_schedule_booking_abandoned", {
    last_completed_step: lastStep,
    timestamp: new Date().toISOString(),
  });
};

/**
 * Track patient type selection
 */
export const trackPatientTypeSelection = (patientType: PatientType) => {
  pushGTMEvent("self_schedule_patient_type_selected", {
    patient_type: formatToHumanReadable(patientType),
  });
};

export const trackBackwardStep = (
  previousStep: SelfScheduleStep | null,
  nextStep: SelfScheduleStep | null,
) => {
  pushGTMEvent("self_schedule_backward_step", {
    previous_step: previousStep,
    next_step: nextStep,
  });
};

/**
 * Track location type selection
 */
export const trackLocationTypeSelection = (
  locationType: "virtual" | "in-person" | "all",
) => {
  pushGTMEvent("self_schedule_location_type_selected", {
    location_type: locationType,
  });
};

/**
 * Track form errors
 */
export const trackFormError = (
  step: SelfScheduleStep,
  errorMessage: string,
) => {
  pushGTMEvent("self_schedule_form_error", {
    step_id: step,
    error_message: errorMessage,
  });
};
